// @ts-nocheck: converted from JS

import Button from '@/components/UI/Button';
import { formatNumberComingIn } from '@/helpers';
import {
  _isBlank,
  _isEmpty,
  _isEmptyString,
  _isNil,
  _isNotBlank,
  _isNotEmpty,
  _isStringifiedNullish,
  _notNil,
  _set,
} from '@/littledash';
import type { ID } from '@/model/Common.model';
import { type DraftFormData, type StudyDraft } from '@/model/Draft.model';
import type { MainUser } from '@/model/User.model';
import { type PlannedAnimalData, PlanTypeMap } from '@/model/PlannedAnimal.model';
import {
  type MetadataFieldTypes,
  type MetadataFieldValueTypes,
  type MetadataFieldWithValue,
  type MetadataGlossaryApiId,
} from '@/model/Metadata.model';
import type { SampleDefinitionFormData } from '@/model/SamplePlan.model';
import { AnimalSexLabelToCode } from '@/model/Animal.utils';
import type { AnimalSexLabel } from '@/model/Animal.model';
import type {
  StudyFormMetadataItem,
  StudyFormMetaDetails,
  StudyFormPayload,
  StudyFormSection,
} from '@/model/StudyForm.model';
import { useThrottle } from '@/support/Hooks/throttle/useThrottle';
import { DateUtils } from '@/utils/Date.utils';
import { type FC, type ReactNode } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { type CreateStudyReducer, type CreateStudyState } from './Create.model';
import {
  useStudyCreateGroups,
  useStudyCreateNavigation,
  useStudyCreateSave,
  useStudyCreateStep,
} from '@/utils/studyCreation/useStudyCreate.tsx';
import { BENCHLING_INTEGRATION_META_SLUG } from '@/helpers.constants.tsx';

interface FormActionsProps {
  state: unknown;
  dispatch: () => void;
  submitForm?: () => void;
  isUseForm?: boolean;
  submitAction?: string;
  disableSaveDraft?: boolean;
  handleSubmitProp?: (callback: (data: unknown) => void) => (e?: React.BaseSyntheticEvent) => Promise<void>;
}

export const FormActions: FC<FormActionsProps> = ({
  state,
  dispatch,
  submitForm,
  isUseForm = false,
  submitAction = 'updateStudy',
  disableSaveDraft = false,
  testKey = 'study-create-form-actions',
  handleSubmitProp,
}) => {
  const formContext = useFormContext();
  const handleSubmit = handleSubmitProp ?? formContext?.handleSubmit;
  const { currentStep, stepReady } = useStudyCreateStep();
  const { changeStep } = useStudyCreateNavigation();
  const { savingDraft, advancingStep, saveDraft } = useStudyCreateSave();
  const { groups, treatments, groupsTreatments, plannedAnimalData } = useStudyCreateGroups();

  const onSubmit = (data, _, draftSaveOnNext = false) => {
    data.links = state.study.links;
    dispatch({
      type: submitAction,
      data: { ...data, files: state.study.files },
    });
    saveDraft({ type: draftSaveOnNext ? 'SAVE_AND_ADVANCE' : 'SAVE' });
  };

  const next = useThrottle({
    func: () => {
      if (isUseForm && handleSubmit) {
        handleSubmit((data) => onSubmit(data, '_', true))();
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      } else if (submitForm) {
        submitForm(state, groups, treatments, groupsTreatments, plannedAnimalData);
      } else {
        saveDraft({ type: 'SAVE_AND_ADVANCE' });
      }
    },
    duration: 500,
  });

  const handleDraftSave = () => {
    if (isUseForm && handleSubmit) {
      handleSubmit((data) => onSubmit(data, '_', false))();
    } else {
      saveDraft({ type: 'SAVE' });
    }
  };

  return (
    <div className="pv3 flex items-center justify-between pr4">
      <div className="flex items-center">
        <Button
          onClick={next}
          className="mr3"
          testId="next-or-create-button"
          disabled={!stepReady || savingDraft || advancingStep}
          testKey={testKey}
        >
          {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
          {submitForm ? 'Create' : 'Next'}
        </Button>
        <Button
          plain
          disabled={currentStep < 1 || savingDraft || advancingStep}
          testId="back-button"
          onClick={() => changeStep({ type: 'PREV_STEP' })}
        >
          Back
        </Button>
      </div>
      {!disableSaveDraft && (
        <Button
          onClick={handleDraftSave}
          disabled={savingDraft || advancingStep}
          stateless
          className="link blue ph0"
          tooltip="Save your progress and continue at another time"
          testId="save-draft-button"
        >
          Save as draft
        </Button>
      )}
    </div>
  );
};

interface UseFormProviderProps {
  state: unknown;
  dispatch: () => void;
  children: ReactNode;
}

export const UseFormProvider: FC<UseFormProviderProps> = ({ state, dispatch, children }) => {
  const methods = useForm({
    shouldUnregister: false,
    defaultValues: {
      ...state.study,
    },
  });
  return (
    <FormProvider {...methods}>
      <form>
        {children}
        <FormActions state={state} dispatch={dispatch} isUseForm handleSubmitProp={methods.handleSubmit} />
      </form>
    </FormProvider>
  );
};

interface PanelLayoutProps {
  children: ReactNode;
  title: string;
  subtitle?: string;
}

export const PanelLayout: FC<PanelLayoutProps> = ({ children, title, subtitle }) => {
  return (
    <>
      <div className="mb4">
        <h3 className="fw5 f5 pb2">{title}</h3>
        {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
        {subtitle && <p>{subtitle}</p>}
      </div>
      <div className="mw7 ui-card">
        <div className="pa4 bb b--moon-gray">{children}</div>
      </div>
    </>
  );
};

const initialState: CreateStudyState = {
  form: {
    loading: true,
    errors: [],
    submitting: false,
    submitted: false,
    saving: false,
    result: {},
    selectableMetadata: [],
    totalCages: 10,
    bulkEdit: false,
    templates: [],
    presets: [],
    selectedPreset: null,
    createCages: true,
    selectedProjectName: '',
    selectedStudyCode: '',
  },
  study: {
    name: '',
    notes: '',
    metadata: [],
    metadataHash: {},
    collections: [],
    author: null,
    owner: null,
    users: [],
    alerts: [],
    files: [],
    average_calculation: 'arithmetic', // arithmetic || geometric
    error_deviation: 'sd', // sd || sem
    tracking_day_base: 0,
    tasks: null,
    started_on: DateUtils.dateNow(),
    preset_id: null,
    enable_consumption: false,
    project_id: '',
    links: [],
    benchling_folder: null,
    timezone: null,
  },
};

export const initialiseState = (
  draft: StudyDraft,
  studyTimezone: string,
  currentUser?: MainUser,
  teamUsers?: Array<MainUser>
): CreateStudyState => {
  let state = { ...initialState };
  if (_isNil(state.study.timezone)) {
    _set(state, 'study.timezone', studyTimezone);
  }
  if (_notNil(draft)) {
    state = {
      ...state,
      study: {
        ...state.study,
        ...draft.form_data,
      },
    };
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (draft.selectedProjectName) {
      state.form.selectedProjectName = draft.selectedProjectName;
    }
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (draft.selectedStudyCode) {
      state.form.selectedStudyCode = draft.selectedStudyCode;
      state.study.study_code = draft.selectedStudyCode;
    }
    if (_notNil(draft.author) && _isNotEmpty(teamUsers)) {
      const draftAuthor = teamUsers.find((user) => Number(user.id) === Number(draft.author));
      if (_notNil(draftAuthor)) {
        state.study.author = draftAuthor;
      }
    }
  }
  if (_isNil(state.study.author) && _notNil(currentUser)) {
    state.study.author = currentUser;
  }
  return state;
};

export const validMultiSelectValue = (metaValue: MetadataFieldValueTypes): boolean => {
  // Empty stringified array is not valid
  if (Array.isArray(metaValue) && metaValue.length > 0) {
    return true;
  }
  try {
    // Check if the string is a valid JSON array
    const parsedValue = JSON.parse(metaValue as string);
    if (Array.isArray(parsedValue) && parsedValue.length > 0) {
      return true;
    }
    // Unpopulated array is not valid
    return false;
  } catch (e) {
    return false;
  }
};

/**
 * Filters draft metadata entries whose glossary field is no longer present in any
 * active custom section of the current study form. The per-section "invalid
 * metadata" banner only fires while the user is on a section that still contains
 * the field, so it cannot recover values for fields that have been removed from
 * the form entirely — those would otherwise be sent to the server and rejected
 * as a BadRequestException.
 */
export const filterStaleStudyMetadata = (
  metadata: Array<{ glossary_id: ID; value: MetadataFieldValueTypes; field_type?: string }> | undefined,
  formMeta: StudyFormMetaDetails | undefined
) => {
  if (_isNil(formMeta) || _isNil(metadata)) {
    return metadata ?? [];
  }
  const formFieldIds = new Set<ID>();
  (formMeta.sections ?? []).forEach((section) => {
    if (section.type === 'custom' && section.active && _isNotEmpty(section.metadata)) {
      section.metadata.forEach((item) => formFieldIds.add(item.info.id));
    }
  });
  return metadata.reduce(
    (acc, meta) => {
      if (formFieldIds.has(meta.glossary_id)) {
        acc.push(meta);
      }
      return acc;
    },
    [] as typeof metadata
  );
};

/**
 * Normalizes a metadata cell value to the shape expected by the studies API.
 * Mirrors form-path `mapMetadata` and v12 HoT `createMeta` behavior so HoT-sourced
 * metadata (planned/registered animals, sample definitions) is safe to submit.
 */
export const normalizeMetadataValueForPayload = (
  fieldType: MetadataFieldTypes | string | undefined,
  value: MetadataFieldValueTypes
): MetadataFieldValueTypes | undefined => {
  if (_isNil(value)) {
    return undefined;
  }
  if (Array.isArray(value) && _isEmpty(value)) {
    return undefined;
  }
  if (typeof value === 'string' && _isBlank(value)) {
    return undefined;
  }

  switch (fieldType) {
    case 'multi_select':
    case 'lookup_multi_select': {
      let normalized: MetadataFieldValueTypes = value;

      if (typeof value === 'string') {
        const trimmed = value.trim();
        // A JSON-array-shaped string (e.g. "[]" or '["a","b"]') must be parsed as JSON, never
        // CSV-split. Otherwise an empty "[]" would become ["[]"] and reach the API as garbage.
        let parsedJsonArray: MetadataFieldValueTypes | undefined;
        if (trimmed.startsWith('[')) {
          try {
            const parsed = JSON.parse(trimmed) as unknown;
            if (Array.isArray(parsed)) {
              parsedJsonArray = parsed as MetadataFieldValueTypes;
            }
          } catch {
            parsedJsonArray = undefined;
          }
        }

        if (_notNil(parsedJsonArray)) {
          normalized = parsedJsonArray;
        } else if (_isNotBlank(value)) {
          normalized = value
            .split(',')
            .map((entry) => entry.trim())
            .filter((entry) => _isNotBlank(entry));
        } else {
          return undefined;
        }
      } else if (Array.isArray(value)) {
        normalized = value;
      }

      // Defense-in-depth: drop blank and coerced-nil sentinel tokens ("undefined"/"null")
      // from every source (CSV, JSON, array). Editor (F1) and the sample provider guard (F2)
      // already strip these, but animal/group/treatment/study multi-selects reach here with
      // no such guard, so a stray sentinel must never ship as ["undefined"].
      if (Array.isArray(normalized)) {
        normalized = normalized.filter(
          (entry) =>
            _notNil(entry) && (typeof entry !== 'string' || (!_isEmptyString(entry) && !_isStringifiedNullish(entry)))
        );
      }

      if (!validMultiSelectValue(normalized)) {
        return undefined;
      }
      // The metadata API contract is `value: string` for every field type (StudyCreateMetadataV1 /
      // SampleDefinitionMetadataV1). Multi-select values round-trip as a JSON-encoded array string
      // (the server returns and MetadataCell parses JSON), so emit that string here, never a raw
      // array. Sending an array makes the strict /planned/samples/mutate endpoint reject the request
      // with 400 "Failed to read request".
      return Array.isArray(normalized) ? JSON.stringify(normalized) : String(normalized);
    }
    case 'numeric': {
      // Number() validates the value (and treats 0 as valid, unlike formatNumberComingIn's
      // truthy guard). Zero is a legitimate numeric value and must not be dropped.
      const numeric = Number(value);
      // Reject NaN and non-finite values (Infinity / -Infinity / overflow like 1e999); the API
      // only accepts finite numerics.
      if (isNaN(numeric) || !isFinite(numeric)) {
        return undefined;
      }
      if (numeric === 0) {
        return '0';
      }
      const formatted = formatNumberComingIn(value);
      return isNaN(formatted) ? undefined : formatted.toString();
    }
    case 'date': {
      const dateString = String(value);
      return _isNotBlank(dateString) ? dateString : undefined;
    }
    case 'select':
    case 'text':
    case 'lookup_select':
    default: {
      if (Array.isArray(value)) {
        return undefined;
      }
      const stringValue = String(value);
      return _isNotBlank(stringValue) ? stringValue : undefined;
    }
  }
};

export const buildSampleMetadataFieldTypeMap = (
  glossary?: Array<{ api_id?: MetadataGlossaryApiId | null; field_type: MetadataFieldTypes }>
): Map<MetadataGlossaryApiId, MetadataFieldTypes> =>
  (glossary ?? []).reduce<Map<MetadataGlossaryApiId, MetadataFieldTypes>>((acc, item) => {
    if (_notNil(item.api_id)) {
      acc.set(item.api_id, item.field_type);
    }
    return acc;
  }, new Map());

export const mapSampleDefinitionMetadata = (
  metadata: SampleDefinitionFormData['metadata'] | undefined,
  fieldTypeByApiId?: Map<MetadataGlossaryApiId, MetadataFieldTypes>
): SampleDefinitionFormData['metadata'] => {
  // Without field types we cannot normalize safely: the default branch treats array values
  // (already-normalized multi-selects, e.g. reloaded from a saved draft) as invalid and drops
  // them, silently losing data. When the glossary is missing/still loading, pass metadata
  // through unchanged rather than risk stripping it.
  if (_isNil(fieldTypeByApiId) || fieldTypeByApiId.size === 0) {
    return metadata ?? [];
  }
  return (metadata ?? []).reduce<SampleDefinitionFormData['metadata']>((acc, item) => {
    const fieldType = fieldTypeByApiId.get(item.type);
    const normalizedValue = normalizeMetadataValueForPayload(fieldType, item.value);

    if (_notNil(normalizedValue)) {
      acc.push({ type: item.type, value: normalizedValue });
    }
    return acc;
  }, []);
};

type HotAnimalMetadataPayloadItem = {
  glossary_id: ID;
  value: MetadataFieldValueTypes;
  title: string;
  field_type: string;
  type: string;
};

export const mapHotAnimalMetadata = (
  metadata: Record<string, MetadataFieldWithValue> | undefined
): Array<HotAnimalMetadataPayloadItem> =>
  Object.entries(metadata ?? {}).reduce<Array<HotAnimalMetadataPayloadItem>>((acc, [id, entry]) => {
    const normalizedValue = normalizeMetadataValueForPayload(entry.field_type, entry.value);

    if (_notNil(normalizedValue)) {
      acc.push({
        glossary_id: entry.glossary_id || Number(id),
        value: normalizedValue,
        title: entry.title || '',
        field_type: entry.field_type || '',
        type: entry.glossary_api_id || '',
      });
    }
    return acc;
  }, []);

export const mapMetadata = (metadata) =>
  (metadata ?? []).reduce((acc, meta) => {
    const normalizedValue = normalizeMetadataValueForPayload(meta.field_type, meta.value);

    if (_notNil(normalizedValue)) {
      acc.push({ ...meta, value: normalizedValue });
    }
    return acc;
  }, []);

export const assemblePayload: (
  study: DraftFormData,
  groups: Array<TreatmentGroup>,
  treatments: Array<Treatment>,
  groupsTreatments: Array<GroupsTreatments>,
  plannedAnimalData: PlannedAnimalData,
  formMeta?: StudyFormMetaDetails,
  formId?: ID,
  sampleMetadataGlossary?: Array<{ api_id?: MetadataGlossaryApiId | null; field_type: MetadataFieldTypes }>
) => StudyFormPayload = (
  study,
  groups,
  treatments,
  groupsTreatments,
  plannedAnimalData,
  formMeta,
  formId,
  sampleMetadataGlossary
) => {
  const sampleMetadataFieldTypes = buildSampleMetadataFieldTypeMap(sampleMetadataGlossary);

  const result = {
    ...study,
    study_code_id: study.study_code?.api_id,
    alerts: study.alerts?.map((a, i) => ({ ...a, id: String(i) })),
    collections: study.collections?.map((c) => ({
      ...c,
      subjects: Number(c.subjects),
    })),
    // Currently adds all metadata options to create for groups
    // This is a workaround to empty those options to prevent HTTP 413
    groups: groups.map((group) => ({
      ...group,
      metadata: mapMetadata(group.metadata),
    })),
    metadata: mapMetadata(filterStaleStudyMetadata(study.metadata, formMeta)),
    type: formMeta?.type,
    study_creation_template_id: formId,
    treatments: treatments?.map((treatment) => ({
      ...treatment,
      treatment_type_id: treatment?.treatment_type?.id,
      metadata: mapMetadata(treatment.metadata),
      treatment_classification: treatment?.treatment_classification ?? 'treatment',
    })),
    groups_treatments: groupsTreatments,
    animals: {
      type: plannedAnimalData.type,
      planned_animals:
        plannedAnimalData.type === PlanTypeMap.planned
          ? (plannedAnimalData?.animals ?? []).map((animal) => ({
              ...animal,
              planned_id: animal.name,
              strain_api_id: animal?.strainApiId ?? null,
              sex: AnimalSexLabelToCode[animal.sex as AnimalSexLabel],
              metadata: mapHotAnimalMetadata(animal.metadata),
            }))
          : null,
      registered_animals:
        plannedAnimalData.type === PlanTypeMap.registered
          ? (plannedAnimalData?.animals ?? []).map((animal) => ({
              ...animal,
              strain_api_id: animal?.strainApiId ?? null,
              sex: AnimalSexLabelToCode[animal.sex as AnimalSexLabel],
              metadata: mapHotAnimalMetadata(animal.metadata),
            }))
          : null,
      cage_animal_count: plannedAnimalData.type === PlanTypeMap.registered ? plannedAnimalData?.animalsPerCage : null,
    },
    sample_definitions: (study.sample_definitions ?? []).map((sampleDefinition) => ({
      ...sampleDefinition,
      metadata: mapSampleDefinitionMetadata(sampleDefinition.metadata, sampleMetadataFieldTypes),
    })),
  };
  if (_isNil(result.project_id) || _isEmptyString(result.project_id)) {
    delete result.project_id;
  }
  delete result.study_code;
  return result;
};

/**
 * Describes the *shape* of a metadata value (never the raw value, to avoid leaking
 * potentially sensitive data into Sentry) so failures like "value not compatible with
 * field type" can be debugged from the breadcrumb alone.
 */
const describeMetadataValueKind = (value: unknown): string => {
  if (value === undefined) {
    return 'undefined';
  }
  if (value === null) {
    return 'null';
  }
  if (Array.isArray(value)) {
    return `array(${value.length})`;
  }
  if (typeof value === 'string') {
    if (value === 'undefined' || value === 'null') {
      return 'sentinel-string';
    }
    if (_isBlank(value)) {
      return 'blank';
    }
    return 'string';
  }
  return typeof value;
};

const SUSPICIOUS_METADATA_KINDS = new Set(['undefined', 'null', 'sentinel-string', 'blank']);

const isSuspiciousMetadataKind = (kind: string): boolean => SUSPICIOUS_METADATA_KINDS.has(kind) || kind === 'array(0)';

/**
 * Builds a compact, Sentry-safe context describing the assembled study-create payload:
 * high-level counts, glossary-load state, an aggregate tally of metadata value kinds, and
 * the set of metadata field types whose values look suspicious. Helps diagnose API-side
 * "Could not create study" / metadata-compatibility errors without dumping the whole body.
 */
export const buildStudyCreateDebugContext = (
  payload: StudyFormPayload | undefined,
  sampleMetadataGlossary?: Array<{ api_id?: MetadataGlossaryApiId | null; field_type: MetadataFieldTypes }>
): Record<string, string> => {
  if (_isNil(payload)) {
    return {};
  }

  const sampleDefinitions = payload.sample_definitions ?? [];
  const animals = [...(payload.animals?.planned_animals ?? []), ...(payload.animals?.registered_animals ?? [])];

  const kindCounts: Record<string, number> = {};
  const suspiciousTypes = new Set<string>();

  const scanMetadata = (
    scope: string,
    entries: Array<{ type?: string; glossary_id?: number | string; value?: unknown }> | undefined
  ) => {
    (entries ?? []).forEach((entry) => {
      const kind = describeMetadataValueKind(entry?.value);
      kindCounts[kind] = (kindCounts[kind] ?? 0) + 1;
      if (isSuspiciousMetadataKind(kind)) {
        suspiciousTypes.add(`${scope}:${entry?.type ?? entry?.glossary_id ?? 'unknown'}=${kind}`);
      }
    });
  };

  sampleDefinitions.forEach((def) => scanMetadata('sample', def?.metadata));
  animals.forEach((animal) => scanMetadata('animal', animal?.metadata));

  return {
    isDraft: `${_notNil(payload.draft_id)}`,
    sampleMetadataGlossaryLoaded: `${_isNotEmpty(sampleMetadataGlossary ?? [])}`,
    sampleMetadataGlossarySize: `${(sampleMetadataGlossary ?? []).length}`,
    groupCount: `${(payload.groups ?? []).length}`,
    treatmentCount: `${(payload.treatments ?? []).length}`,
    sampleDefinitionCount: `${sampleDefinitions.length}`,
    animalCount: `${animals.length}`,
    metadataKindCounts: JSON.stringify(kindCounts),
    suspiciousMetadata: suspiciousTypes.size > 0 ? [...suspiciousTypes].join(', ') : 'none',
  };
};

export const buildCustomSectionDefaultFormData = (section: StudyFormSection, state: CreateStudyState) =>
  Object.values(section.metadata).reduce<Record<string, string>>((acc, metadataItem) => {
    acc[metadataItem.info.id] =
      state?.study?.metadata?.find((stateMetadataItem) => stateMetadataItem.glossary_id === metadataItem.info.id)
        ?.value ?? '';
    return acc;
  }, {});

export interface MetadataValidationResult {
  invalidFields: Record<string, string>;
  clearedFormData: Record<string, string>;
  hasInvalidFields: boolean;
}

export const validateMetadataSelectOptions = (metadataItem: StudyFormMetadataItem, currentValue: unknown): boolean => {
  const fieldType = metadataItem.info.field_type;

  if (fieldType === 'multi_select') {
    let valuesToCheck: Array<string> = [];

    if (Array.isArray(currentValue) && _isNotEmpty(currentValue)) {
      valuesToCheck = currentValue;
    } else if (typeof currentValue === 'string' && _isNotBlank(currentValue)) {
      // Drafts loaded from the API store multi_select values as JSON-encoded strings (e.g. '["optionA"]').
      // Parse before checking option validity so stale values in draft submissions are caught.
      try {
        const parsed = JSON.parse(currentValue.trim()) as unknown;
        if (Array.isArray(parsed) && _isNotEmpty(parsed)) {
          valuesToCheck = parsed as Array<string>;
        }
      } catch {
        // Not valid JSON; nothing to validate
      }
    }

    return (
      _isNotEmpty(valuesToCheck) &&
      valuesToCheck.some((val) => _notNil(val) && !metadataItem.info.options.includes(val))
    );
  } else if (fieldType === 'select') {
    if (typeof currentValue === 'string' && _isNotBlank(currentValue)) {
      return !metadataItem.info.options.includes(currentValue);
    }
    return false;
  }

  return false;
};

export const validateAndClearInvalidMetadata = (
  section: StudyFormSection,
  state: CreateStudyState
): MetadataValidationResult => {
  const initialFormData = buildCustomSectionDefaultFormData(section, state);
  const invalid: Record<ID, string> = {};
  const cleared: Record<string, string> = {};

  Object.values(section.metadata).forEach((metadataItem) => {
    // Ignore benchling folder metas
    if (metadataItem.info.slug === BENCHLING_INTEGRATION_META_SLUG) {
      cleared[metadataItem.info.id] = initialFormData[metadataItem.info.id];
    } else {
      const fieldType = metadataItem.info.field_type;
      if ((fieldType === 'select' || fieldType === 'multi_select') && _isNotEmpty(metadataItem.info.options)) {
        const currentValue = initialFormData[metadataItem.info.id];
        const hasInvalidOption = validateMetadataSelectOptions(metadataItem, currentValue);

        if (hasInvalidOption) {
          invalid[metadataItem.info.id] = metadataItem.info.title;
          cleared[metadataItem.info.id] = '';
        } else {
          cleared[metadataItem.info.id] = currentValue;
        }
      } else {
        cleared[metadataItem.info.id] = initialFormData[metadataItem.info.id];
      }
    }
  });

  return {
    invalidFields: invalid,
    clearedFormData: cleared,
    hasInvalidFields: _isNotEmpty(Object.keys(invalid)),
  };
};

export const clearInvalidMetadataFromState = (
  invalidFieldIds: Array<string>,
  dispatch: (action: unknown) => void
): void => {
  const metadataToUpdate = invalidFieldIds.reduce<Record<string, string>>((acc, metadataId) => {
    acc[metadataId] = '';
    return acc;
  }, {});

  dispatch({
    type: 'updateMetadata',
    data: { metadata: metadataToUpdate },
  });
};

export const reducer: CreateStudyReducer = (state, action) => {
  switch (action.type) {
    case 'updateForm':
      return {
        ...state,
        form: {
          ...state.form,
          ...action.data,
        },
      };
    case 'setErrors':
      return {
        ...state,
        form: {
          ...state.form,
          errors: action.data,
        },
      };
    case 'setPreset':
      return {
        ...state,
        form: {
          ...state.form,
          selectedPreset: action.data,
        },
        study: {
          ...state.study,
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          preset_id: action.data?.id || null,
        },
      };
    case 'updateStudy':
      return {
        ...state,
        study: {
          ...state.study,
          ...action.data,
        },
      };
    case 'updateMetadata': {
      // @Todo temporary fix that needs time to refactor
      // discussion to be had about this entire section;
      // using arrays is really difficult/wasteful in here
      const submittedMetadata = Object.entries(action.data.metadata ?? []).map(([key, val]) => {
        const glossary_id = Number(key);
        const field_type = state.study.metadataHash[glossary_id]?.field_type ?? '';

        let value = val;
        if (field_type === 'text') {
          value = _isNil(val) ? val : String(val).trim();
        }
        return {
          glossary_id,
          value: value,
          field_type,
        };
      });

      const combined = [...state.study.metadata, ...submittedMetadata];
      const updatedMetadata = [...new Map(combined.map((item) => [item['glossary_id'], item])).values()];

      return {
        ...state,
        study: {
          ...state.study,
          metadata: updatedMetadata,
        },
      };
    }
    case 'SET_SELECTABLE_METADATA': {
      let metadata = state.study.metadata;
      const metadataHash = {};

      // Always populate metadataHash for all selectable metadata
      action.data.forEach(({ id, field_type }) => {
        metadataHash[id] = { field_type, glossary_id: id };
      });

      if (_isEmpty(state.study.metadata)) {
        metadata = action.data.reduce((acc, { id, required_by, field_type }) => {
          if (required_by !== 'optional') {
            acc.push({ glossary_id: id, value: '', field_type });
          }
          return acc;
        }, []);
      }
      return {
        form: {
          ...state.form,
          selectableMetadata: action.data,
        },
        study: {
          ...state.study,
          metadata,
          metadataHash,
        },
      };
    }
    case 'update':
      return {
        ...state,
        ...action.data,
      };
    case 'add-task': {
      return {
        ...state,
        study: { ...state.study, tasks: { ...(state.study.tasks ?? null), ...action.data } },
      };
    }
    case 'delete-task': {
      const tasks = action.data.reduce(
        (acc, id) => {
          delete acc[id];
          return { ...acc };
        },
        { ...(state.study.tasks ?? {}) }
      );
      return {
        ...state,
        study: { ...state.study, tasks },
      };
    }
    case 'update-timezone': {
      return { ...state, study: { ...state.study, timezone: action.data } };
    }
    default: {
      return { ...state };
    }
  }
};
